import { useEffect, useState, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { DateRange } from 'react-day-picker';
import Tabs from '../Tabs/Tabs';
import Tab from '../Tabs/Tab';
import { logger } from '../../error-tracker';
import { flushLocalForage, getLocalForage, setLocalForage } from '../../aaa';
import { isUserHasKey, logDebugLevel } from '../../GlobalFunctions';
import * as XLSX from 'xlsx';
import {
  getClassrooms,
  getDistricts,
  getGameProgress as getGameProgressFromDb,
  getJob,
  getJournals as getJournalsFromDb,
  isMixpanelQueryOnlyReportsEnabled,
  getSchools,
  getUsers,
  queryMixpanelByGroup,
  queryMixpanelByGroupV2,
  listStudentAssignments,
} from '../../BackendInterface';
import {
  aggregateGameProgressByStudent,
  getDateBeginningOfSchoolYear,
  getDefaultSchoolYear,
  getDefaultSchoolYearMinusOne,
  getGameProgressQueryStartDate,
  getListOfSchoolYears,
  getStudentsToLoginsMap,
  getTimestampFromDate,
  getToday,
  getStartOfSchoolYear,
  getUpdatedSchoolUnifiedScript,
  getLegacySchoolPlatformScript,
  getLegacySchoolPeekavilleScript,
  filterMixpanelEventsByDate,
  filterStudentObjectByDate,
  getMixpanelDatabaseByDate,
  getCurrentYearTeachersData,
  getLegacyEducatorLogins,
  getNewEducatorLogins,
  getLegacyTimeOnPlatform,
  getNewTimeOnPlatform,
  getLegacyStudentLogins,
  getNewStudentLogins,
  getLegacyStoryOpens,
  getNewStoryOpens,
  getTotalRow,
  lookupNewDBStartDate,
  getLegacyStoryOpenEvents,
  getNewStoryOpenEvents,
} from '../reportUtilities';
import SpinnerOverlay from '../../SpinnerOverlay';
import DateRangePicker from '../../components/DateRangePicker';
import SchoolChart from './SchoolChart';
import SchoolDashboard from './SchoolDashboard';
import DistrictSelector from '../AdminReportComponents/DistrictSelector';
import SchoolSelector from '../AdminReportComponents/SchoolSelector';
import AnalyticsOverview from './AnalyticsOverview';
import StudentAnalytics from './StudentAnalytics';
import TeacherAnalytics from './TeacherAnalytics';
import CustomDropdown from '../AdminReportComponents/CustomDropdown';
import styles from './SchoolReport.module.scss';
import { RootState } from '../../ApplicationState';
import {
  District,
  ReportStudent,
  ReportingMixpanelEvent,
  SchoolsList,
  TeacherClassroomMap,
  TeacherClassroomMaps,
  TeacherStudentMap,
} from '@peekapak/types/ReportDataExchangeTypes';
import { ClassroomType, SchoolModel, UserProfileType } from '@peekapak/types/DataProtocolTypes';
import { PeekavilleJournalEntry } from '@peekapak/types/PeekavilleGameDataTypes';
import { Assignment } from '@peekapak/types/LessonPlanTypes';
import { date } from 'yup';
import associationData from '../../ClassroomDashboard/Associations.json';

export interface SchoolReportState {
  completionsReady: boolean;
  legacyPlatformEvents: ReportingMixpanelEvent[];
  legacyPlatformEventsReady: boolean;
  legacyPeekavilleEvents: ReportingMixpanelEvent[];
  legacyPeekavilleEventsReady: boolean;
  newMixpanelEvents: ReportingMixpanelEvent[];
  newMixpanelEventsReady: boolean;
  disableChangeLocation?: boolean;
  districtsList: District[];
  districtsListReady: boolean;
  errorMessage: string;
  gameProgressMap: Record<string, unknown>;
  gameProgressMapReady: boolean;
  gameProgressMapLoadProgress: string;
  journalsMap: Record<string, PeekavilleJournalEntry[]>;
  journalsMapReady: boolean;
  journalsMapLoadProgress?: string;
  assignmentsMap: Record<string, Assignment[]>;
  assignmentsMapReady: boolean;
  assignmentsMapLoadProgress?: string;
  schoolsList: SchoolsList;
  selectedDistrict: string;
  schoolsListReady: boolean;
  selectedSchool: string;
  selectedSchoolYear: string;
  selectedSchoolYearStartingDate: string;
  selectedSchoolYearEndingDate: string;
  teacherClassroomMaps: TeacherClassroomMaps;
  teacherClassroomMapsReady: boolean;
  teacherStudentMap: Record<string, ReportStudent[]>;
  teacherStudentMapReady: boolean;
  userList: {
    schoolId: string;
    list: UserProfileType[];
  };
  userListProgress: string;
  userListReady: boolean;
  loadingStatus: string;
  useMixpanelReports: boolean;
}

const tableOverrideSuffix = '';

const SchoolReport = () => {
  // initialize state
  const today = new Date();
  const startOfSchoolYear = getStartOfSchoolYear();
  const initialDateRange: DateRange = { from: startOfSchoolYear, to: today };

  const [dateRange, setDateRange] = useState<DateRange>(initialDateRange);
  const [isDisplayDayPicker, setIsDisplayDayPicker] = useState(false);
  const [dateFilteredJournals, setDateFilteredJournals] = useState({});
  const [dateFilteredAssignments, setDateFilteredAssignments] = useState({});
  const [dateFilteredLegacyPlatformEvents, setDateFilteredLegacyPlatformEvents] = useState<ReportingMixpanelEvent[]>(
    [],
  );
  const [dateFilteredLegacyPeekavilleEvents, setDateFilteredLegacyPeekavilleEvents] = useState<
    ReportingMixpanelEvent[]
  >([]);
  const [dateFilteredNewMixpanelEvents, setDateFilteredNewMixpanelEvents] = useState<ReportingMixpanelEvent[]>([]);
  const [eventToChart, setEventToChart] = useState<string>('user_logins');

  const userProfile = useSelector((state: RootState) => state.user.userProfile);
  const location = useLocation<{ schoolId?: string } | undefined>();
  const navigatedSchoolId = location.state?.schoolId;
  const userManuallySelectedRef = useRef(false);

  const startDate = dateRange.from;
  const endDate = dateRange.to;

  const eventNames = [
    {
      name: 'Active Users',
      id: 'active_users',
    },
    {
      name: 'User Logins',
      id: 'user_logins',
    },
    {
      name: 'Lessons Accessed',
      id: 'lessons_accessed',
    },
    {
      name: 'Story Opens',
      id: 'story_opens',
    },
    {
      name: 'Time on Platform (hrs)',
      id: 'time_on_platform',
    },
  ];

  const getCommonInitialState = () => {
    return {
      completionsMap: {},
      completionsReady: false,
      legacyPlatformEvents: [],
      legacyPlatformEventsReady: false,
      disableChangeLocation: true,
      errorMessage: '',
      gameProgressMap: {},
      gameProgressMapLoadProgress: '',
      gameProgressMapReady: false,
      journalsMap: {},
      journalsMapLoadProgress: '',
      journalsMapReady: false,
      assignmentsMap: {},
      assignmentsMapLoadProgress: '',
      assignmentsMapReady: false,
      legacyPeekavilleEvents: [],
      legacyPeekavilleEventsReady: false,
      teacherClassroomMaps: {
        school: '',
        maps: [],
      },
      teacherClassroomMapsReady: false,
      teacherStudentMap: {},
      teacherStudentMapReady: false,
      userList: {
        list: [],
        schoolId: '',
      },
      userListProgress: '',
      userListReady: false,
      newMixpanelEvents: [],
      newMixpanelEventsReady: false,
    };
  };

  const getDistrictInitialState = () => {
    return {
      districtsList: [],
      districtsListReady: false,
      selectedDistrict: '',
    };
  };

  const getSchoolInitialState = () => {
    return {
      schoolsList: {
        district: '',
        list: [],
      },
      schoolsListReady: false,
      selectedSchool: '',
    };
  };

  const getStudentAnalyticsInitialState = () => {
    return {
      studentAnalyticsReportType: 'usage',
      studentAnalyticsSelectedTeacher: '',
      studentAnalyticsSelectedClassroom: '',
    };
  };

  const getSchoolYearInitialState = () => {
    return {
      selectedSchoolYear: getDefaultSchoolYear(),
      selectedSchoolYearStartingDate: getDateBeginningOfSchoolYear(),
      selectedSchoolYearEndingDate: getToday(),
    };
  };

  const [state, setState] = useState<SchoolReportState>({
    ...getCommonInitialState(),
    ...getDistrictInitialState(),
    ...getSchoolInitialState(),
    ...getSchoolYearInitialState(),
    loadingStatus: 'Loading...',
    useMixpanelReports: true,
  });

  // Fetch the feature flag once on mount. The URL param ?useNewReports=true
  // overrides the ApplicationConfiguration value for internal QA.
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const urlOverride = urlParams.get('useNewReports');
    if (urlOverride === 'true') {
      setState((prevState) => ({ ...prevState, useMixpanelReports: true }));
      return;
    }
    if (urlOverride === 'false') {
      setState((prevState) => ({ ...prevState, useMixpanelReports: false }));
      return;
    }
    const districtId = userProfile?.district || '';
    const schoolId = userProfile?.schoolId || '';
    isMixpanelQueryOnlyReportsEnabled(districtId, schoolId).then((enabled) => {
      setState((prevState) => ({ ...prevState, useMixpanelReports: enabled }));
    });
  }, [userProfile?.district, userProfile?.schoolId]);

  const handleBeforeStart = () =>
    setState((prevState) => ({
      ...prevState,
      selectedSchoolYear: getDefaultSchoolYearMinusOne(),
    }));

  //
  // get list of districts
  //
  useEffect(() => {
    let mounted = true;

    if (!state.districtsListReady) {
      getListOfDistricts();
    } else {
      restoreSavedData();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getListOfDistricts() {
      const storageKey = 'districtsList';
      const map: District[] = (await getLocalForage(storageKey)) as District[];

      if (map && map.length > 0 && mounted) {
        return setState((prevState) => ({
          ...prevState,
          districtsList: map,
          districtsListReady: true,
        }));
      }

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          loadingStatus: 'Loading districts...',
        }));
      }

      const mapFromDb = await getDistricts();
      await setLocalForage(storageKey, mapFromDb, 1440);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          districtsList: mapFromDb,
          districtsListReady: true,
        }));
      }
    }

    async function restoreSavedData() {
      const storageKey = 'selectedDistrict';
      const selectedDistrict = await getLocalForage(storageKey);

      if (selectedDistrict) {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            selectedDistrict,
          }));
        }
      }
    }
  }, [
    state.districtsList,
    state.districtsListReady,
    userProfile?.district,
    userProfile?.schoolId,
    state.selectedDistrict,
  ]);
  //
  // schools in district
  //
  useEffect(() => {
    let mounted = true;
    if (!state.schoolsListReady) getListOfSchoolsInDistrict();
    return function cleanup() {
      mounted = false;
    };

    // load list of schools
    async function getListOfSchoolsInDistrict() {
      const districtStorageKey = 'selectedDistrict';
      const district =
        state.selectedDistrict || (await getLocalForage(districtStorageKey)) || userProfile?.district || '';
      if (!district) return;
      const storageKey = `${district}-schoolsList`;
      const list = await (async () => {
        const schoolsList = (await getLocalForage(storageKey)) as SchoolModel[];
        if (schoolsList?.length > 0) {
          return schoolsList.filter((s: SchoolModel) => !s.isArchived);
        }
        return null;
      })();

      if (list && mounted) {
        return setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list,
          },
          schoolsListReady: true,
        }));
      }

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          loadingStatus: 'Loading schools...',
        }));
      }

      const listFromDb = await (async () => {
        const list = await getSchools(district, tableOverrideSuffix);
        if (list?.length > 0) {
          return list.filter((s) => !s.isArchived);
        }

        return [];
      })();

      await setLocalForage(storageKey, listFromDb, 43200);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          schoolsList: {
            district,
            list: listFromDb,
          },
          schoolsListReady: true,
        }));
      }
    }
  }, [userProfile, state.schoolsListReady, state.selectedDistrict, state.districtsListReady]);
  //
  // get list of users in selected school
  //
  useEffect(() => {
    let mounted = true;
    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (state.selectedSchool && state.selectedSchoolYear) {
        const fromStorageForAll = await getLocalForage(`${state.selectedSchool}-users`);

        if (fromStorageForAll && mounted) {
          setState((prevState) => ({
            ...prevState,
            userList: {
              list: fromStorageForAll,
              schoolId: state.selectedSchool,
            },
            userListReady: true,
          }));
        } else {
          getUsersInSchool();
        }
      }
    }

    async function getUsersInSchool() {
      // console.debug(
      //   `%cGetting db users from `,
      //   'background: blue; color: yellow',
      //   state.selectedSchool
      // );
      //
      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          loadingStatus: 'Loading users...',
        }));
      }

      const jobId = await getUsers(state.selectedSchool, tableOverrideSuffix);
      let job: {
        status: string;
        data: string;
      } = await getJob(jobId);

      try {
        while (isJobPending(job)) {
          processPendingJob(job);
          await yieldAndWait();
          job = await getJob(jobId);
        }
      } catch (e) {
        const err = e as Error;
        setState((prevState) => ({
          ...prevState,
          errorMessage: err.message,
        }));
        logger.logException(err);
        console.error(err);

        throw err;
      }

      const data = JSON.parse(job.data);

      if (job.status === 'Done') {
        await setLocalForage(`${state.selectedSchool}-users`, data, 10080);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            userList: {
              list: data,
              schoolId: state.selectedSchool,
            },
            userListReady: true,
          }));
        }
      } else if (job.status === 'Error') {
        if (mounted) {
          if (job.data) {
            const data = JSON.parse(job.data);
            setState((prevState) => ({
              ...prevState,
              errorMessage: data.message,
            }));
            logger.logException(new Error(data.message));
          } else {
            const e = new Error('Unknown error occurred while trying to retrieve list of users');
            setState((prevState) => ({
              ...prevState,
              errorMessage: e.message,
            }));
            logger.logException(e);
          }
        }
      } else {
        throw new Error(`Unexpected job status ${job.status}`);
      }
    }

    function isJobPending(job: { status: string }) {
      return job.status !== 'Done' && job.status !== 'Error';
    }

    function processPendingJob(job: { data: string }) {
      if (job.data) {
        const data = JSON.parse(job.data);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            userListProgress: `Found ${data.numberOfResults} users`,
          }));
        }
      }
    }

    function yieldAndWait() {
      return new Promise<void>((resolve) => {
        setTimeout(() => resolve(), 1000);
      });
    }
  }, [
    state.selectedSchool,
    state.selectedSchoolYear,
    state.selectedSchoolYearEndingDate,
    state.selectedSchoolYearStartingDate,
  ]);

  //
  // set selected school from navigation
  //
  useEffect(() => {
    if (navigatedSchoolId && state.schoolsListReady && state.schoolsList.list.length > 0) {
      const schoolExists = state.schoolsList.list.some((s) => s.id === navigatedSchoolId);
      if (schoolExists) {
        const district = state.selectedDistrict || userProfile?.district;
        const storageKey = `${district}-selectedSchool`;
        setLocalForage(storageKey, navigatedSchoolId);
      }
    }
  }, [navigatedSchoolId, state.schoolsListReady, state.selectedDistrict, userProfile?.district]);

  //
  // set selected school
  //
  useEffect(() => {
    run();

    async function run() {
      if (userManuallySelectedRef.current) return;
      if (state.selectedDistrict && state.schoolsListReady && state.schoolsList.list.length > 0) {
        const district = state.selectedDistrict || userProfile?.district;
        const storageKey = `${district}-selectedSchool`;
        const selectedSchool = await (async () => {
          // if user is not part of a district return nothing
          if (!userProfile?.district) {
            return '';
          }

          // if navigated from district report, use that school ID directly
          // but only if the user has not manually selected a school via the dropdown
          if (
            !userManuallySelectedRef.current &&
            navigatedSchoolId &&
            state.schoolsList.list.some((s) => s.id === navigatedSchoolId)
          ) {
            await setLocalForage(storageKey, navigatedSchoolId);
            return navigatedSchoolId;
          }

          // if the user is part of Peekapak management and can select district then the user's profile's schoolId may be a part of a district that is not the currently selected district
          if (isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')) {
            if (state.schoolsListReady) {
              if (userProfile?.schoolId && state.schoolsList.list.map((s) => s.id).includes(userProfile.schoolId)) {
                return (await getLocalForage(storageKey)) || userProfile.schoolId;
              }

              return (await getLocalForage(storageKey)) || state.schoolsList.list[0].id;
            }

            return '';
          }

          // if user is part of district and can change schools then return cache
          // or userProfile school Id
          if (isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')) {
            return (await getLocalForage(storageKey)) || userProfile?.schoolId || state.schoolsList.list[0].id;
          }

          // if user is part of district but cannot change schools, then return
          // userProfile's schoolId or blank
          return (await getLocalForage(storageKey)) || userProfile.schoolId || '';
        })();

        setState((prevState) => ({ ...prevState, selectedSchool }));

        return;
      }

      setState((prevState) => ({ ...prevState, selectedSchool: '' }));
    }
  }, [
    state.schoolsListReady,
    state.schoolsList.list,
    state.selectedDistrict,
    navigatedSchoolId,
    userProfile?.district,
    userProfile?.schoolId,
  ]);
  //
  // set selected district
  //
  useEffect(() => {
    run();

    async function run() {
      if (state.districtsListReady && state.districtsList.length > 0) {
        const storageKey = 'selectedDistrict';
        const selectedDistrict = await getLocalForage(storageKey);

        if (selectedDistrict) {
          setState((prevState) => ({ ...prevState, selectedDistrict }));
        } else {
          if (userProfile?.district) {
            if (state.districtsList.map((d) => d.id).includes(userProfile.district)) {
              return setState((prevState) => ({
                ...prevState,
                selectedDistrict: userProfile.district!,
              }));
            }
          }
          /*
        setState((prevState) => ({
          ...prevState,
          selectedDistrict: state.districtsList[0].id,
        }));
        */
        }

        return;
      }

      setState((prevState) => ({ ...prevState, selectedDistrict: '' }));
    }
  }, [userProfile?.district, state.districtsListReady, state.districtsList]);

  //
  // get selected school year starting and ending date (as query params)
  //
  useEffect(() => {
    let selectedSchoolYearStartDate: string;
    let selectedSchoolYearEndDate: string;

    const thisYear = new Date(Date.now()).getFullYear();
    const today = getToday();
    const listOfSchoolYears = getListOfSchoolYears();

    listOfSchoolYears?.forEach((schoolYearObj) => {
      if (state.selectedSchoolYear === schoolYearObj.id) {
        selectedSchoolYearStartDate = `${schoolYearObj.startingYear}-07-01`;
        selectedSchoolYearEndDate =
          schoolYearObj.startingYear === thisYear.toString() ? `${today}` : `${schoolYearObj.endingYear}-06-30`;
      }
    });

    setState((prevState) => ({
      ...prevState,
      selectedSchoolYearStartingDate: selectedSchoolYearStartDate,
      selectedSchoolYearEndingDate: selectedSchoolYearEndDate,
    }));
  }, [state.selectedSchoolYear]);
  //
  // get teacher analytics
  //
  useEffect(() => {
    let mounted = true;
    const selectedSchoolYearStartingDate = state.selectedSchoolYearStartingDate;
    const selectedSchoolYearEndingDate = state.selectedSchoolYearEndingDate;

    run();
    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (state.selectedSchool && state.schoolsListReady) {
        const data = await getLocalForage(
          `${state.selectedSchool}-legacyPlatformEvents-${selectedSchoolYearStartingDate}-${selectedSchoolYearEndingDate}`,
        );
        if (!data) {
          doQuery();
        } else {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              legacyPlatformEvents: data,
              legacyPlatformEventsReady: true,
            }));
          }
        }
      }
    }
    async function doQuery() {
      const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === state.selectedSchool);

      if (!selectedSchoolObject)
        throw new Error(`Unexpected condition: could not find a matching school object for selected school in report`);

      // console.debug(
      //   `%cGetting teacher login data from `,
      //   'background: blue; color: yellow',
      //   state.selectedSchool
      // );
      try {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            loadingStatus: 'Loading analytics...',
          }));
        }

        const result = await (async () => {
          const startingDateAsDate = new Date(selectedSchoolYearStartingDate);
          if (startingDateAsDate > lookupNewDBStartDate('platformEvents')) {
            return Promise.resolve([]);
          }
          return await queryMixpanelByGroup(
            getLegacySchoolPlatformScript(
              selectedSchoolYearStartingDate,
              selectedSchoolYearEndingDate,
              selectedSchoolObject.combineIds || selectedSchoolObject.id,
            ),
            'peekapak',
            tableOverrideSuffix,
          );
        })();

        await setLocalForage(
          `${state.selectedSchool}-legacyPlatformEvents-${selectedSchoolYearStartingDate}-${selectedSchoolYearEndingDate}`,
          result,
          60,
        );

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            legacyPlatformEvents: result,
            legacyPlatformEventsReady: true,
          }));
        }
      } catch (error) {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            errorMessage: (error as Error).message,
          }));
        }
        logger.logException(error as Error);
        console.error(error);

        throw error;
      }
    }
  }, [
    state.selectedSchool,
    state.selectedSchoolYear,
    state.selectedSchoolYearEndingDate,
    state.selectedSchoolYearStartingDate,
  ]);

  //get new mixpanel DB analytics data

  useEffect(() => {
    let mounted = true;
    const selectedSchoolYearStartingDate = state.selectedSchoolYearStartingDate;
    const selectedSchoolYearEndingDate = state.selectedSchoolYearEndingDate;
    const storageKey = `${state.selectedSchool}-newMixpanelEvents-v2-from-${selectedSchoolYearStartingDate}-to-${selectedSchoolYearEndingDate}`;

    run();

    return function cleanup() {
      mounted = false;
    };

    async function run() {
      if (state.selectedSchool && state.schoolsListReady) {
        const data = await getLocalForage(storageKey);
        if (!data) {
          doQuery();
        } else {
          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              newMixpanelEvents: data,
              newMixpanelEventsReady: true,
            }));
          }
        }
      }
    }
    async function doQuery() {
      const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === state.selectedSchool);

      if (!selectedSchoolObject)
        throw new Error(`Unexpected condition: could not find a matching school object for selected school in report`);

      try {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            loadingStatus: 'Loading analytics...',
          }));
        }
        const result = await queryMixpanelByGroupV2(
          getUpdatedSchoolUnifiedScript(
            selectedSchoolYearStartingDate,
            selectedSchoolYearEndingDate,
            selectedSchoolObject.combineIds || selectedSchoolObject.id,
          ),
          'unified',
          tableOverrideSuffix,
        );

        await setLocalForage(storageKey, result, 60);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            newMixpanelEvents: result,
            newMixpanelEventsReady: true,
          }));
        }
      } catch (error) {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            errorMessage: (error as Error).message,
          }));
        }
        logger.logException(error as Error);
        console.error(error);

        throw error;
      }
    }
  }, [
    state.selectedSchool,
    state.selectedSchoolYear,
    state.selectedSchoolYearEndingDate,
    state.selectedSchoolYearStartingDate,
  ]);

  // //
  // // get completion data
  // //
  // useEffect(() => {
  //   let mounted = true;

  //   if (
  //     state.userListReady &&
  //     state.teacherClassroomMapsReady &&
  //     !state.completionsReady
  //   ) {
  //     const completions = await getLocalForage(
  //       `${state.selectedSchool}-completions-from-${state.selectedSchoolYearStartingDate}-to-${state.selectedSchoolYearEndingDate}`
  //     );

  //     if (!completions) {
  //       doCompletionQuery();
  //     } else {
  //       if (mounted) {
  //         setState((prevState) => ({
  //           ...prevState,
  //           completionsReady: true,
  //           completionsMap: completions,
  //         }));
  //       }
  //     }
  //   }

  //   return function cleanup() {
  //     mounted = false;
  //   };

  //   async function doCompletionQuery() {
  //     // console.debug(
  //     //   `%cGetting teacher completion data from `,
  //     //   'background: blue; color: yellow',
  //     //   state.userList.schoolId
  //     // );
  //     let i = 0;
  //     const completions = {};

  //     const teachers = getCurrentYearTeachersData(
  //       state.userList.list,
  //       state.teacherClassroomMaps,
  //       state.legacyPlatformEvents,
  //       state.newMixpanelEvents
  //     );

  //     while (i < teachers.length) {
  //       const emailToQueryOn = teachers[i].email;
  //       try {
  //         const results = await getCompletedLessonsCounts(
  //           emailToQueryOn,
  //           state.selectedSchoolYear.split('-')[0],
  //           tableOverrideSuffix
  //         );
  //         completions[emailToQueryOn] = results;
  //       } catch {
  //         completions[emailToQueryOn] = {};
  //       }

  //       i += 1;
  //     }

  //     await setLocalForage(
  //       `${state.selectedSchool}-completions-from-${state.selectedSchoolYearStartingDate}-to-${state.selectedSchoolYearEndingDate}`,
  //       completions,
  //       60
  //     );

  //     if (mounted) {
  //       setState((prevState) => ({
  //         ...prevState,
  //         completionsReady: true,
  //         completionsMap: completions,
  //       }));
  //     }
  //   }
  // }, [
  //   state.teacherClassroomMaps,
  //   state.teacherClassroomMapsReady,
  //   state.userListReady,
  //   state.selectedSchool,
  //   state.completionsReady,
  //   state.legacyPlatformEvents,
  //   state.userList,
  //   state.legacyPeekavilleEvents,
  //   state.selectedSchoolYear,
  //   state.selectedSchoolYearStartingDate,
  //   state.selectedSchoolYearEndingDate,
  // ]);
  //
  // teacher to student map
  //
  useEffect(() => {
    let mounted = true;

    if (!state.teacherStudentMapReady || !state.teacherClassroomMapsReady) {
      if (state.userListReady) {
        getTeacherStudentClassroomMap();
      }
    }

    return function cleanup() {
      mounted = false;
    };

    async function getTeacherStudentClassroomMap() {
      // console.debug(
      //   `%cGetting teacher student map data from `,
      //   'background: blue; color: yellow',
      //   state.userList.schoolId
      // );
      const teacherStudentMapStorageKey = `${state.selectedSchool}-teacherStudentMap-from-${state.selectedSchoolYearStartingDate}-to-${state.selectedSchoolYearEndingDate}`;
      const teacherClassroomMapsStorageKey = `${state.selectedSchool}-teacherClassroomMaps-from-${state.selectedSchoolYearStartingDate}-to-${state.selectedSchoolYearEndingDate}`;
      const teacherStudentMap = JSON.parse((await getLocalForage(teacherStudentMapStorageKey)) as string);
      const teacherClassroomMaps = JSON.parse((await getLocalForage(teacherClassroomMapsStorageKey)) as string);

      if (teacherStudentMap && teacherClassroomMaps && mounted) {
        return setState((prevState) => ({
          ...prevState,
          teacherStudentMap,
          teacherClassroomMaps,
          teacherStudentMapReady: true,
          teacherClassroomMapsReady: true,
        }));
      }

      const teachers = state.userList.list.filter(
        (e) => e.type === 'Teacher' || e.type === 'SchoolAdmin' || e.type === 'DistrictAdmin',
      );
      const teacherToStudentsMap: TeacherStudentMap = {};
      const teacherToClassroomMapList: TeacherClassroomMap[] = [];
      let i = 0;

      while (i < teachers.length) {
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            loadingStatus: `Loading classroom data part ${i + 1}/${teachers.length}...`,
          }));
        }

        const classrooms = await getClassrooms(teachers[i].email, tableOverrideSuffix);

        const list: ReportStudent[] = [];

        classrooms.forEach((c) => {
          const startingTimestamp = getTimestampFromDate(state.selectedSchoolYearStartingDate);
          const endingTimestamp = getTimestampFromDate(state.selectedSchoolYearEndingDate);
          if (c.createdAt >= startingTimestamp && c.createdAt <= endingTimestamp) {
            if (c.listOfStudentProxies) {
              c.listOfStudentProxies.forEach((p) => {
                const { firstName, middleName, lastName, userId, username } = p;
                const studentToStore = {
                  firstName,
                  middleName: middleName || '',
                  lastName: lastName || '',
                  userId,
                  username,
                  grade: parseInt(c.grade),
                  language: c.language,
                };

                list.push(studentToStore);
              });
            }
          }
        });

        teacherToStudentsMap[teachers[i].email] = list;

        // NB: there are three types of entries
        // (1) teacher with no classrooms -> these will be considered "Other"
        //     i.e. classrooms will be empty list
        // (2) teacher with classrooms -> these will be considered "Rostered"
        //     i.e. classrooms could be without students, like the Sample
        // (3) teacher with classrooms and students -> these will be considered "Rostered"
        teacherToClassroomMapList.push({
          name: `${teachers[i].firstName} ${teachers[i].lastName}`,
          email: teachers[i].email,
          userId: teachers[i].userId,
          classrooms,
        });

        i += 1;
      }

      await setLocalForage(teacherStudentMapStorageKey, JSON.stringify(teacherToStudentsMap), 1440);

      teacherToClassroomMapList.sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));

      const newTeacherClassroomMaps = {
        school: state.selectedSchool,
        maps: teacherToClassroomMapList,
      };

      await setLocalForage(teacherClassroomMapsStorageKey, JSON.stringify(newTeacherClassroomMaps), 60);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          teacherClassroomMaps: newTeacherClassroomMaps,
          teacherStudentMap: teacherToStudentsMap,
          teacherStudentMapReady: true,
          teacherClassroomMapsReady: true,
        }));
      }
    }
  }, [
    state.selectedSchool,
    state.teacherStudentMapReady,
    state.teacherClassroomMapsReady,
    state.userListReady,
    state.legacyPlatformEvents,
    state.userList,
    state.legacyPeekavilleEvents,
    state.newMixpanelEvents,
    state.selectedSchoolYear,
    state.selectedSchoolYearStartingDate,
    state.selectedSchoolYearEndingDate,
  ]);

  //
  // get legacy peekaville events for this school
  //
  useEffect(() => {
    let mounted = true;

    if (
      state.schoolsListReady &&
      state.teacherStudentMapReady &&
      state.selectedSchool &&
      !state.legacyPeekavilleEventsReady
    ) {
      getlegacyPeekavilleEvents();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getlegacyPeekavilleEvents() {
      // console.debug(
      //   `%cGetting mypeekaville mixpanel events from `,
      //   'background: blue; color: yellow',
      //   state.selectedSchool
      // );
      //
      // don't store this in local storage because it
      // may exceed the size limit causing all of the
      // storage to get erased
      //
      // const storageKey = `${state.selectedSchool}-legacyPeekavilleEvents`;
      // const list = await getLocalForage(storageKey);
      // if (list) {
      //   return setState((prevState) => ({
      //     ...prevState,
      //     legacyPeekavilleEvents: list,
      //     legacyPeekavilleEventsReady: true,
      //   }));
      // }
      //
      let results: ReportingMixpanelEvent[];
      if (
        state.schoolsListReady &&
        state.selectedSchoolYear &&
        state.selectedSchoolYearStartingDate &&
        state.selectedSchoolYearEndingDate
      ) {
        const selectedSchoolObject = state.schoolsList.list.find((s) => s.id === state.selectedSchool);

        if (!selectedSchoolObject) {
          throw new Error(
            `Unexpected condition: could not find a matching school object for selected school in report`,
          );
        }

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            loadingStatus: 'Loading analytics...',
          }));
        }

        const resultsRaw = await queryMixpanelByGroup(
          getLegacySchoolPeekavilleScript(
            state.selectedSchoolYearStartingDate,
            state.selectedSchoolYearEndingDate,
            selectedSchoolObject.combineIds || selectedSchoolObject.id,
          ),
          'mypeekaville',
          tableOverrideSuffix,
        );

        results = resultsRaw;
      }

      //
      // don't store this in local storage because it
      // may exceed the size limit causing all of the
      // storage to get erased
      //
      // await setLocalForage(storageKey, results, 30);
      //
      //
      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          legacyPeekavilleEvents: results,
          legacyPeekavilleEventsReady: true,
        }));
      }
    }
  }, [
    state.selectedSchool,
    state.selectedSchoolYear,
    state.selectedSchoolYearEndingDate,
    state.selectedSchoolYearStartingDate,
    state.teacherStudentMapReady,
  ]);
  //
  // get students' game progress
  //
  useEffect(() => {
    let mounted = true;

    if (
      state.userListReady &&
      state.selectedSchool &&
      state.legacyPeekavilleEventsReady &&
      state.newMixpanelEventsReady &&
      !state.gameProgressMapReady
    ) {
      getGameProgress();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getGameProgress() {
      const storageKey = `${state.selectedSchool}-gameProgress-from-${state.selectedSchoolYearStartingDate}-to-${state.selectedSchoolYearEndingDate}`;
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          gameProgressMap: map,
          gameProgressMapReady: true,
        }));
      }

      if (state.useMixpanelReports) {
        //
        // NEW PATH: aggregate game progress events already fetched in
        // newMixpanelEvents — no per-student DynamoDB calls needed.
        //
        // Floor the start date to the Mixpanel cutover so we never ask
        // Mixpanel for data it doesn't have (pre Aug 1 2023).
        //
        const effectiveStart = getGameProgressQueryStartDate(state.selectedSchoolYearStartingDate);
        const gameEvents = state.newMixpanelEvents.filter(
          (e) =>
            (e.name === 'journal-quest-complete' ||
              e.name === 'journal-graded-mini-game-complete' ||
              e.name === 'journal-book-complete' ||
              e.name === 'journal-reading-comprehension-complete') &&
            e.time >= new Date(effectiveStart).getTime(),
        );
        const newMap = aggregateGameProgressByStudent(gameEvents);

        await setLocalForage(storageKey, newMap, 1440);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            gameProgressMap: newMap,
            gameProgressMapReady: true,
          }));
        }
      } else {
        //
        // LEGACY PATH: per-student DynamoDB queries.
        // Retained for rollback capability behind the feature flag.
        //
        // build map of student ids to number of events so that we can
        // filter out students who are not active, since querying
        // dynamoDB is expensive
        const studentIdToLoginsMap = getStudentsToLoginsMap([
          ...state.legacyPeekavilleEvents,
          ...state.newMixpanelEvents,
        ]);
        // query the database for each student
        const students = state.userList.list.filter(
          (u) => u.type === 'Student' && studentIdToLoginsMap[u.userId as keyof typeof studentIdToLoginsMap],
        );
        let i = 0;
        const newMap: Record<string, ClassroomType[]> = {};

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            loadingStatus: 'Loading game progress...',
          }));
        }

        while (i < students.length) {
          const progress = await getGameProgressFromDb(students[i].userId, tableOverrideSuffix);
          newMap[students[i].userId] = progress;

          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              gameProgressMapLoadProgress: `${i + 1} of ${students.length}`,
            }));
          }

          i += 1;
        }

        await setLocalForage(storageKey, newMap, 1440);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            gameProgressMap: newMap,
            gameProgressMapReady: true,
          }));
        }
      }
    }
  }, [
    state.userList.schoolId,
    state.selectedSchool,
    state.gameProgressMapReady,
    state.userListReady,
    state.userList.list,
    state.selectedSchoolYearStartingDate,
    state.selectedSchoolYearEndingDate,
    state.legacyPeekavilleEvents,
    state.legacyPeekavilleEventsReady,
    state.newMixpanelEvents,
    state.newMixpanelEventsReady,
    state.useMixpanelReports,
  ]);
  //
  // get students' journal entries
  //
  useEffect(() => {
    let mounted = true;

    if (
      state.userListReady &&
      state.selectedSchool &&
      state.legacyPeekavilleEventsReady &&
      state.newMixpanelEventsReady &&
      !state.journalsMapReady
    ) {
      getJournals();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getJournals() {
      const storageKey = `${state.selectedSchool}-journals-from-${state.selectedSchoolYearStartingDate}-to-${state.selectedSchoolYearEndingDate}`;
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          journalsMap: map,
          journalsMapReady: true,
        }));
      }

      const DBtoQuery = getMixpanelDatabaseByDate(dateRange.from as Date, dateRange.to as Date);
      if (DBtoQuery === 'newDB') {
        const newMixpanelJournalEvents = state.newMixpanelEvents.filter(
          (e) => e.name === 'journal-mood-log' && e.type === 'Student',
        );
        const newMap = newMixpanelJournalEvents.reduce((acc, curr) => {
          const { userId } = curr;
          if (!acc[userId]) {
            acc[userId] = [];
          }
          acc[userId].push(curr);
          return acc;
        }, {});
        await setLocalForage(storageKey, newMap, 1440);
        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: newMap,
            journalsMapReady: true,
          }));
        }
      } else if (DBtoQuery === 'legacyDB') {
        // build map of student ids to number of events so that we can
        // filter out students who are not active, since querying
        // dynamoDB is expensive
        const studentIdToLoginsMap = getStudentsToLoginsMap([
          ...state.legacyPeekavilleEvents,
          ...state.legacyPlatformEvents,
        ]);
        // query the database for each student
        const students = state.userList.list.filter(
          (u) => u.type === 'Student' && studentIdToLoginsMap[u.userId as keyof typeof studentIdToLoginsMap],
        );
        let i = 0;
        const newMap: Record<string, PeekavilleJournalEntry[]> = {};

        while (i < students.length) {
          const journals = await getJournalsFromDb(students[i].userId, tableOverrideSuffix);
          newMap[students[i].userId] = journals;

          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              journalsMapLoadProgress: `${i + 1} of ${students.length}`,
            }));
          }

          i += 1;
        }

        await setLocalForage(storageKey, newMap, 1440);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: newMap,
            journalsMapReady: true,
          }));
        }
      } else if (DBtoQuery === 'mixed') {
        const newMixpanelJournalEvents = state.newMixpanelEvents.filter(
          (e) => e.name === 'journal-mood-log' && e.type === 'Student',
        );
        const newMixpanelJournalMap = newMixpanelJournalEvents.reduce((acc, curr) => {
          const { userId } = curr;
          if (!acc[userId]) {
            acc[userId] = [];
          }
          acc[userId].push(curr);
          return acc;
        }, {});
        // build map of student ids to number of events so that we can
        // filter out students who are not active, since querying
        // dynamoDB is expensive
        const studentIdToLoginsMap = getStudentsToLoginsMap([
          ...state.legacyPeekavilleEvents,
          ...state.legacyPlatformEvents,
        ]);
        // query the database for each student
        const students = state.userList.list.filter(
          (u) => u.type === 'Student' && studentIdToLoginsMap[u.userId as keyof typeof studentIdToLoginsMap],
        );
        let i = 0;
        const legacyJournalMap: Record<string, PeekavilleJournalEntry[]> = {};

        while (i < students.length) {
          const journals = await getJournalsFromDb(students[i].userId, tableOverrideSuffix);
          legacyJournalMap[students[i].userId] = journals;

          if (mounted) {
            setState((prevState) => ({
              ...prevState,
              journalsMapLoadProgress: `${i + 1} of ${students.length}`,
            }));
          }

          i += 1;
        }
        const combinedJournalsMap = {
          ...newMixpanelJournalMap,
          ...legacyJournalMap,
        };

        await setLocalForage(storageKey, combinedJournalsMap, 1440);

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            journalsMap: combinedJournalsMap,
            journalsMapReady: true,
          }));
        }
      }
    }
  }, [
    state.legacyPeekavilleEvents,
    state.legacyPeekavilleEventsReady,
    state.newMixpanelEvents,
    state.newMixpanelEventsReady,
    state.userList.schoolId,
    state.selectedSchool,
    state.journalsMapReady,
    state.userListReady,
    state.userList.list,
    state.selectedSchoolYearStartingDate,
    state.selectedSchoolYearEndingDate,
  ]);
  //
  // get student assignments
  //
  useEffect(() => {
    let mounted = true;

    if (
      state.userListReady &&
      state.selectedSchool &&
      state.legacyPeekavilleEventsReady &&
      state.teacherStudentMap &&
      !state.assignmentsMapReady
    ) {
      getAssignments();
    }

    return function cleanup() {
      mounted = false;
    };

    async function getAssignments() {
      const storageKey = `${state.selectedSchool}-assignments-from-${state.selectedSchoolYearStartingDate}-to-${state.selectedSchoolYearEndingDate}`;
      const map = await getLocalForage(storageKey);

      if (map && mounted) {
        return setState((prevState) => ({
          ...prevState,
          assignmentsMap: map,
          assignmentsMapReady: true,
        }));
      }

      // console.debug(
      //   `%cGetting student journal entries from `,
      //   'background: blue; color: yellow',
      //   state.userList.schoolId
      // );
      // build map of student ids to number of events so that we can
      // filter out students who are not active, since querying
      // dynamoDB is expensive

      const students = await Object.values(state.teacherStudentMap).reduce((acc, curr) => {
        const secondaryOnly = curr.filter((item) => parseInt(item.grade) > 5);
        return [...acc, ...secondaryOnly];
      }, []);

      let i = 0;
      const newMap: Record<string, Assignment[]> = {};

      while (i < students.length) {
        try {
          const assignments = await listStudentAssignments(
            students[i].userId,
            students[i].language,
            students[i].grade,
            tableOverrideSuffix,
          );
          newMap[students[i].userId] = assignments;
        } catch (e) {
          console.error(e);
        }

        if (mounted) {
          setState((prevState) => ({
            ...prevState,
            assignmentsMapLoadProgress: `${i + 1} of ${students.length}`,
          }));
        }

        i += 1;
      }

      await setLocalForage(storageKey, newMap, 1440);

      if (mounted) {
        setState((prevState) => ({
          ...prevState,
          assignmentsMap: newMap,
          assignmentsMapReady: true,
        }));
      }
    }
  }, [
    state.legacyPeekavilleEvents,
    state.legacyPeekavilleEventsReady,
    state.userList.schoolId,
    state.selectedSchool,
    state.assignmentsMapReady,
    state.userListReady,
    state.userList.list,
    state.selectedSchoolYearStartingDate,
    state.selectedSchoolYearEndingDate,
  ]);
  //
  // disable drop downs when data is being retrieved
  //
  useEffect(() => {
    if (
      (!state.schoolsListReady ||
        !state.userListReady ||
        !state.legacyPlatformEventsReady ||
        !state.districtsListReady ||
        !state.teacherStudentMapReady ||
        !state.legacyPeekavilleEventsReady ||
        !state.gameProgressMapReady ||
        !state.assignmentsMapReady ||
        !state.journalsMapReady ||
        !state.newMixpanelEventsReady) &&
      !state.errorMessage
    ) {
      return setState((prevState) => ({
        ...prevState,
        disableChangeLocation: true,
      }));
    }

    return setState((prevState) => ({
      ...prevState,
      disableChangeLocation: false,
    }));
  }, [
    state.schoolsListReady,
    state.userListReady,
    state.legacyPlatformEventsReady,
    state.newMixpanelEventsReady,
    state.districtsListReady,
    state.teacherStudentMapReady,
    state.legacyPeekavilleEventsReady,
    state.gameProgressMapReady,
    state.journalsMapReady,
    state.assignmentsMapReady,
    state.errorMessage,
  ]);

  //
  // set error conditions
  //
  useEffect(() => {
    if (userProfile && !userProfile.district && !isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (userProfile?.district && !isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL') && !userProfile.schoolId) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No data available',
      }));
    }

    if (state.schoolsListReady && state.schoolsList.list.length === 0) {
      setState((prevState) => ({
        ...prevState,
        disableChangeLocation: false,
        errorMessage: 'No school data applicable',
      }));
    }
  }, [userProfile, state.schoolsListReady, state.schoolsList]);

  const filterDataByDate = useCallback(async () => {
    if (state.journalsMapReady) {
      const journals = filterStudentObjectByDate(startDate as Date, endDate as Date, state.journalsMap);
      setDateFilteredJournals(journals);
    }
    if (state.assignmentsMapReady) {
      const assignments = filterStudentObjectByDate(startDate as Date, endDate as Date, state.assignmentsMap);
      setDateFilteredAssignments({ ...assignments });
    }
    if (state.legacyPeekavilleEventsReady) {
      const newEvents = filterMixpanelEventsByDate(startDate as Date, endDate as Date, state.legacyPeekavilleEvents);
      setDateFilteredLegacyPeekavilleEvents(newEvents);
    }
    if (state.legacyPlatformEventsReady) {
      const platformEvents = filterMixpanelEventsByDate(startDate as Date, endDate as Date, state.legacyPlatformEvents);
      setDateFilteredLegacyPlatformEvents(platformEvents);
    }
    if (state.newMixpanelEventsReady) {
      const unifiedMixpanelEvents = filterMixpanelEventsByDate(
        startDate as Date,
        endDate as Date,
        state.newMixpanelEvents,
      );
      setDateFilteredNewMixpanelEvents(unifiedMixpanelEvents);
    }
  }, [
    dateRange,
    state.journalsMap,
    state.assignmentsMap,
    state.legacyPeekavilleEvents,
    state.legacyPlatformEvents,
    state.newMixpanelEvents,
    state.journalsMapReady,
    state.assignmentsMapReady,
    state.legacyPeekavilleEventsReady,
    state.legacyPlatformEventsReady,
    state.newMixpanelEventsReady,
  ]);

  useEffect(() => {
    filterDataByDate();
  }, [
    state.journalsMap,
    state.assignmentsMap,
    state.legacyPeekavilleEvents,
    state.legacyPlatformEvents,
    state.newMixpanelEvents,
    state.journalsMapReady,
    state.assignmentsMapReady,
    state.legacyPeekavilleEventsReady,
    state.legacyPlatformEventsReady,
    state.newMixpanelEventsReady,
  ]);

  useEffect(() => {
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getStudentAnalyticsInitialState(),
      ...getSchoolYearInitialState(),
      ...getDistrictInitialState(),
    }));
  }, [dateRange]);

  const isReportLoading =
    !state.legacyPeekavilleEventsReady ||
    !state.legacyPlatformEventsReady ||
    !state.newMixpanelEventsReady ||
    !state.userListReady ||
    !state.teacherStudentMapReady ||
    !state.teacherClassroomMapsReady ||
    !state.gameProgressMapReady ||
    !state.journalsMapReady ||
    !state.assignmentsMapReady;

  const handleDownloadExcel = useCallback(async () => {
    if (isReportLoading || !state.selectedSchool) return;

    try {
      const schoolName = state.schoolsList.list.find((s) => s.id === state.selectedSchool)?.name || 'School';
      const startDateStr = dateRange.from ? dateRange.from.toLocaleDateString().replace(/\//g, '-') : '';
      const endDateStr = dateRange.to ? dateRange.to.toLocaleDateString().replace(/\//g, '-') : '';

      const workbook = XLSX.utils.book_new();

      const allEvents = [
        ...(state.legacyPeekavilleEvents || []),
        ...(state.legacyPlatformEvents || []),
        ...(state.newMixpanelEvents || []),
      ];

      const activeTeachers = getCurrentYearTeachersData(
        state.userList.list || [],
        state.teacherClassroomMaps,
        state.legacyPlatformEvents || [],
        state.newMixpanelEvents || [],
      );

      const studentsByTeacher: Record<string, any[]> = state.teacherStudentMap || {};

      const rosteredStudentIds = new Set(
        Object.values(studentsByTeacher)
          .flat()
          .map((s) => s.userId),
      );
      const allStudentEventIds = [
        ...new Set(
          allEvents
            .filter((e) => e.type === 'Student')
            .map((e) => e.userId || e.distinct_id)
            .filter(Boolean),
        ),
      ];
      const otherStudentIds = allStudentEventIds.filter((id) => !rosteredStudentIds.has(id));
      const otherStudentEvents = allEvents.filter(
        (e) => (e.userId || e.distinct_id) && otherStudentIds.includes(e.userId || e.distinct_id),
      );

      const overviewHeaders = [
        'School',
        'Staff Count',
        'Student Count',
        'Lessons',
        'Activities',
        'Stories',
        'Check-ins',
        'Assignments',
      ];
      const staffCount = activeTeachers.length;
      const studentCount = Object.values(studentsByTeacher).reduce((acc, students) => acc + students.length, 0);
      const lessonsCount = allEvents.filter((e) => e.name === 'lessons_open').length;
      const activitiesCount = allEvents.filter((e) => e.name === 'game-play' || e.name === 'mini-game-complete').length;
      const storiesCount = allEvents.filter(
        (e) => e.name === 'story_opens' || e.name === 'book_opens' || e.name === 'journal_book_open',
      ).length;
      const checkinsCount = allEvents.filter((e) => e.name === 'journal-mood-log').length;
      const assignmentsCount = Object.values(state.assignmentsMap || {}).flat().length;

      const overviewData = [
        overviewHeaders,
        [
          schoolName,
          staffCount,
          studentCount,
          lessonsCount,
          activitiesCount,
          storiesCount,
          checkinsCount,
          assignmentsCount,
        ],
      ];
      const overviewSheet = XLSX.utils.aoa_to_sheet(overviewData);
      XLSX.utils.book_append_sheet(workbook, overviewSheet, 'Overview');

      const summaryStudentsHeaders = ["Teacher's Name", 'Number of Students', 'Student Logins', 'Feelings Check-Ins'];
      const summaryStudentsRows: (string | number)[][] = [];
      const sortedTeachers = [...activeTeachers].sort((a, b) => (a.lastName || '').localeCompare(b.lastName || ''));

      sortedTeachers.forEach((teacher) => {
        const teacherStudents = studentsByTeacher[teacher.email] || [];
        const studentLogins = teacherStudents.reduce((acc, student) => {
          const studentEvents = allEvents.filter(
            (e) => e.userId === student.userId || e.distinct_id === student.userId,
          );
          return acc + getLegacyStudentLogins(studentEvents) + getNewStudentLogins(studentEvents);
        }, 0);
        const feelingsCheckIns = teacherStudents.reduce((acc, student) => {
          const journals = state.journalsMap?.[student.userId] || [];
          return acc + journals.length;
        }, 0);

        summaryStudentsRows.push([
          `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
          teacherStudents.length,
          studentLogins,
          feelingsCheckIns,
        ]);
      });
      if (summaryStudentsRows.length > 0) {
        const studentsData = [summaryStudentsHeaders, ...summaryStudentsRows];
        const studentsSheet = XLSX.utils.aoa_to_sheet(studentsData);
        XLSX.utils.book_append_sheet(workbook, studentsSheet, 'Students');
      }

      // Other (Unassociated) students
      if (otherStudentIds.length > 0) {
        const otherFeelingsCheckIns = otherStudentIds.reduce(
          (acc, id) => acc + ((state.journalsMap?.[id] as any[])?.length || 0),
          0,
        );
        const existingStudentsSheetIdx = workbook.SheetNames.indexOf('Students');
        if (existingStudentsSheetIdx >= 0) {
          const existingSheet = workbook.Sheets[workbook.SheetNames[existingStudentsSheetIdx]];
          const existingRows: (string | number)[][] = XLSX.utils.sheet_to_json(existingSheet, { header: 1 });
          existingRows.push([
            'Other (Unassociated)',
            otherStudentIds.length,
            getLegacyStudentLogins(otherStudentEvents) + getNewStudentLogins(otherStudentEvents),
            otherFeelingsCheckIns,
          ]);
          const newSheet = XLSX.utils.aoa_to_sheet(existingRows);
          workbook.Sheets[workbook.SheetNames[existingStudentsSheetIdx]] = newSheet;
        }
      }

      const elementaryStudentsHeaders = [
        "Teacher's Name",
        'Number of Students',
        'Student Logins',
        'Activities Completed',
        'Book Opens',
        'Feelings Check-Ins',
      ];
      const elementaryStudentsRows: (string | number)[][] = [];

      sortedTeachers.forEach((teacher) => {
        const teacherStudents = (studentsByTeacher[teacher.email] || []).filter((s) => (s.grade || 0) < 6);
        const studentLogins = teacherStudents.reduce((acc, student) => {
          const studentEvents = allEvents.filter(
            (e) => e.userId === student.userId || e.distinct_id === student.userId,
          );
          return acc + getLegacyStudentLogins(studentEvents) + getNewStudentLogins(studentEvents);
        }, 0);
        const activitiesCompleted = teacherStudents.reduce((acc, student) => {
          const gameProgress = state.gameProgressMap?.[student.userId] || [];
          return (
            acc +
            gameProgress.reduce((gameAcc, g: any) => {
              if (g.type === 'mini-game' && g.completionPercent > 0) return gameAcc + 1;
              return gameAcc;
            }, 0)
          );
        }, 0);
        const bookOpens = teacherStudents.reduce((acc, student) => {
          const studentEvents = allEvents.filter(
            (e) => e.userId === student.userId || e.distinct_id === student.userId,
          );
          return acc + getLegacyStoryOpens(studentEvents) + getNewStoryOpens(studentEvents);
        }, 0);
        const feelingsCheckIns = teacherStudents.reduce((acc, student) => {
          const journals = state.journalsMap?.[student.userId] || [];
          return acc + journals.length;
        }, 0);

        elementaryStudentsRows.push([
          `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
          teacherStudents.length,
          studentLogins,
          activitiesCompleted,
          bookOpens,
          feelingsCheckIns,
        ]);
      });
      if (elementaryStudentsRows.length > 0) {
        const elementaryData = [elementaryStudentsHeaders, ...elementaryStudentsRows];
        const elementarySheet = XLSX.utils.aoa_to_sheet(elementaryData);
        XLSX.utils.book_append_sheet(workbook, elementarySheet, 'Students Elementary');
      }

      if (otherStudentIds.length > 0) {
        const otherElemActivities = otherStudentIds.reduce((acc, id) => {
          const progress = state.gameProgressMap?.[id] || [];
          return acc + progress.filter((g: any) => g.type === 'mini-game' && g.completionPercent > 0).length;
        }, 0);
        const otherElemBookOpens = getLegacyStoryOpens(otherStudentEvents) + getNewStoryOpens(otherStudentEvents);
        const otherElemFeelings = otherStudentIds.reduce(
          (acc, id) => acc + ((state.journalsMap?.[id] as any[])?.length || 0),
          0,
        );
        const existingElemSheetIdx = workbook.SheetNames.indexOf('Students Elementary');
        if (existingElemSheetIdx >= 0) {
          const existingSheet = workbook.Sheets[workbook.SheetNames[existingElemSheetIdx]];
          const existingRows: (string | number)[][] = XLSX.utils.sheet_to_json(existingSheet, { header: 1 });
          existingRows.push([
            'Other (Unassociated)',
            otherStudentIds.length,
            getLegacyStudentLogins(otherStudentEvents) + getNewStudentLogins(otherStudentEvents),
            otherElemActivities,
            otherElemBookOpens,
            otherElemFeelings,
          ]);
          const newSheet = XLSX.utils.aoa_to_sheet(existingRows);
          workbook.Sheets[workbook.SheetNames[existingElemSheetIdx]] = newSheet;
        }
      }

      const secondaryStudentsHeaders = [
        "Teacher's Name",
        'Number of Students',
        'Student Logins',
        'Assignments Completed',
        'Story Opens',
        'Feelings Check-Ins',
      ];
      const secondaryStudentsRows: (string | number)[][] = [];

      sortedTeachers.forEach((teacher) => {
        const teacherStudents = (studentsByTeacher[teacher.email] || []).filter((s) => (s.grade || 0) > 5);
        const studentLogins = teacherStudents.reduce((acc, student) => {
          const studentEvents = allEvents.filter((e) => e.userId === student.userId);
          return acc + getLegacyStudentLogins(studentEvents) + getNewStudentLogins(studentEvents);
        }, 0);
        const assignments = teacherStudents.reduce((acc, student) => {
          const studentAssignments = state.assignmentsMap?.[student.userId] || [];
          return acc + studentAssignments.filter((a: any) => a.status === 'submitted').length;
        }, 0);
        const storyOpens = teacherStudents.reduce((acc, student) => {
          const studentEvents = allEvents.filter((e) => e.userId === student.userId);
          return acc + getLegacyStoryOpens(studentEvents) + getNewStoryOpens(studentEvents);
        }, 0);
        const feelingsCheckIns = teacherStudents.reduce((acc, student) => {
          const journals = state.journalsMap?.[student.userId] || [];
          return acc + journals.length;
        }, 0);

        secondaryStudentsRows.push([
          `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
          teacherStudents.length,
          studentLogins,
          assignments,
          storyOpens,
          feelingsCheckIns,
        ]);
      });
      if (secondaryStudentsRows.length > 0) {
        const secondaryData = [secondaryStudentsHeaders, ...secondaryStudentsRows];
        const secondarySheet = XLSX.utils.aoa_to_sheet(secondaryData);
        XLSX.utils.book_append_sheet(workbook, secondarySheet, 'Students Secondary');
      }

      if (otherStudentIds.length > 0) {
        const otherSecAssignments = otherStudentIds.reduce((acc, id) => {
          const studentAssignments = state.assignmentsMap?.[id] || [];
          return acc + studentAssignments.filter((a: any) => a.status === 'submitted').length;
        }, 0);
        const otherSecStoryOpens = getLegacyStoryOpens(otherStudentEvents) + getNewStoryOpens(otherStudentEvents);
        const otherSecFeelings = otherStudentIds.reduce(
          (acc, id) => acc + ((state.journalsMap?.[id] as any[])?.length || 0),
          0,
        );
        const existingSecSheetIdx = workbook.SheetNames.indexOf('Students Secondary');
        if (existingSecSheetIdx >= 0) {
          const existingSheet = workbook.Sheets[workbook.SheetNames[existingSecSheetIdx]];
          const existingRows: (string | number)[][] = XLSX.utils.sheet_to_json(existingSheet, { header: 1 });
          existingRows.push([
            'Other (Unassociated)',
            otherStudentIds.length,
            getLegacyStudentLogins(otherStudentEvents) + getNewStudentLogins(otherStudentEvents),
            otherSecAssignments,
            otherSecStoryOpens,
            otherSecFeelings,
          ]);
          const newSheet = XLSX.utils.aoa_to_sheet(existingRows);
          workbook.Sheets[workbook.SheetNames[existingSecSheetIdx]] = newSheet;
        }
      }

      const staffDataMap: Record<string, { events: typeof allEvents; assignments: any[] }> = {};
      activeTeachers.forEach((teacher) => {
        const teacherEvents = allEvents.filter(
          (event) => event.userId === teacher.userId || event.distinct_id === teacher.email,
        );
        const assignmentsList = (studentsByTeacher[teacher.email] || []).reduce((acc, student) => {
          return state.assignmentsMap?.[student.userId] ? [...acc, ...state.assignmentsMap[student.userId]] : acc;
        }, [] as any[]);
        staffDataMap[teacher.email] = { events: teacherEvents, assignments: assignmentsList };
      });

      const countLessonsFromEvents = (events: typeof allEvents) => {
        const elementaryLessonRegexp = /.*lessonPlan.*(bigIdea|readAloud).*Step1\s/;
        const secondaryLessonRegexp = /^lessonPlan.+lesson/gm;
        return events.reduce((acc, e) => {
          return (e.name.search(elementaryLessonRegexp) >= 0 &&
            new Date(e.time) <= lookupNewDBStartDate('completeDate')) ||
            e.name === 'lessons_open' ||
            (e.name.search(secondaryLessonRegexp) >= 0 && new Date(e.time) <= lookupNewDBStartDate('completeDate'))
            ? acc + 1
            : acc;
        }, 0);
      };

      const countStoryOpensFromEvents = (events: typeof allEvents) => {
        const elementaryBookOpenRegexp = new RegExp(/^story.+pg1\s/gm);
        return events.reduce((acc, e) => {
          return (e.name.search(elementaryBookOpenRegexp) >= 0 &&
            new Date(e.time) <= lookupNewDBStartDate('completeDate')) ||
            e.name === 'lessons_book_open' ||
            e.name === 'journal_book_open' ||
            e.name === 'lessons_story_open' ||
            e.name.includes('featuredstory')
            ? acc + 1
            : acc;
        }, 0);
      };

      const getTeacherLogins = (email: string) => {
        const teacher = staffDataMap[email];
        if (teacher?.events && teacher.events.length > 0) {
          return getLegacyEducatorLogins(teacher.events) + getNewEducatorLogins(teacher.events);
        }
        return 0;
      };

      const getTeacherLessonsAccessed = (email: string) => {
        const teacher = staffDataMap[email];
        if (!teacher?.events) return 0;
        return countLessonsFromEvents(teacher.events);
      };

      const getTeacherStoryOpens = (email: string) => {
        const teacher = staffDataMap[email];
        if (!teacher?.events) return 0;
        return countStoryOpensFromEvents(teacher.events);
      };

      const getTeacherTimeOnPlatform = (email: string) => {
        const teacher = staffDataMap[email];
        if (teacher?.events && teacher.events.length > 0) {
          return getLegacyTimeOnPlatform(teacher.events) + getNewTimeOnPlatform(teacher.events);
        }
        return 0;
      };

      const staffHeaders = ['Full Name', 'Teacher Logins', 'Lessons Accessed', 'Story Opens', 'Time on Platform (hrs)'];
      const staffRows: (string | number)[][] = [];

      sortedTeachers.forEach((teacher) => {
        staffRows.push([
          `${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(),
          getTeacherLogins(teacher.email),
          getTeacherLessonsAccessed(teacher.email),
          getTeacherStoryOpens(teacher.email),
          getTeacherTimeOnPlatform(teacher.email),
        ]);
      });

      if (staffRows.length > 0) {
        const staffData = [staffHeaders, ...staffRows];
        const staffSheet = XLSX.utils.aoa_to_sheet(staffData);
        XLSX.utils.book_append_sheet(workbook, staffSheet, 'Staff');
      }

      // Other (Unassociated) staff
      const rosteredStaffIdSet = new Set(activeTeachers.map((t: any) => t.userId));
      const rosteredStaffEmailSet = new Set(activeTeachers.map((t: any) => t.email));
      const allStaffEvents = [...(state.legacyPlatformEvents || []), ...(state.newMixpanelEvents || [])].filter((e) =>
        ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(e.type),
      );
      const otherStaffEvents = allStaffEvents.filter(
        (e) => !rosteredStaffIdSet.has(e.userId) && !rosteredStaffEmailSet.has(e.distinct_id),
      );
      if (otherStaffEvents.length > 0) {
        const existingStaffSheetIdx = workbook.SheetNames.indexOf('Staff');
        if (existingStaffSheetIdx >= 0) {
          const existingSheet = workbook.Sheets[workbook.SheetNames[existingStaffSheetIdx]];
          const existingRows: (string | number)[][] = XLSX.utils.sheet_to_json(existingSheet, { header: 1 });
          existingRows.push([
            'Other (Unassociated)',
            getLegacyEducatorLogins(otherStaffEvents) + getNewEducatorLogins(otherStaffEvents),
            countLessonsFromEvents(otherStaffEvents),
            countStoryOpensFromEvents(otherStaffEvents),
            +(getLegacyTimeOnPlatform(otherStaffEvents) + getNewTimeOnPlatform(otherStaffEvents)).toFixed(1),
          ]);
          const newSheet = XLSX.utils.aoa_to_sheet(existingRows);
          workbook.Sheets[workbook.SheetNames[existingStaffSheetIdx]] = newSheet;
        }
      }

      const topEngagedHeaders = ['Teacher Name', 'Logins'];
      const topEngagedRows: (string | number)[][] = [];

      sortedTeachers.forEach((teacher) => {
        const logins = getTeacherLogins(teacher.email);
        if (logins > 0) {
          topEngagedRows.push([`${teacher.firstName || ''} ${teacher.lastName || ''}`.trim(), logins]);
        }
      });

      const totalStaffEvents = [
        ...(state.legacyPlatformEvents || []).filter((e) =>
          ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(e.type),
        ),
        ...(state.newMixpanelEvents || []).filter((e) => ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(e.type)),
      ];
      const totalEducatorLogins = getLegacyEducatorLogins(totalStaffEvents) + getNewEducatorLogins(totalStaffEvents);
      const rosteredLoginSum = topEngagedRows.reduce((sum, row) => sum + (row[1] as number), 0);
      const otherLogins = totalEducatorLogins - rosteredLoginSum;

      if (otherLogins > 0) {
        topEngagedRows.push(['Other (Unassociated)', otherLogins]);
      }

      topEngagedRows.sort((a, b) => (b[1] as number) - (a[1] as number));

      const topEngagedSliced = topEngagedRows.slice(0, 5);

      if (topEngagedSliced.length > 0) {
        const topEngagedData = [topEngagedHeaders, ...topEngagedSliced];
        const topEngagedSheet = XLSX.utils.aoa_to_sheet(topEngagedData);
        XLSX.utils.book_append_sheet(workbook, topEngagedSheet, 'Top Engaged Teachers');
      }

      // Day of Week Educators Login
      const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const educatorLoginEvents: ReportingMixpanelEvent[] = [];
      const allStaffEventsForDay = [...(state.legacyPlatformEvents || []), ...(state.newMixpanelEvents || [])];
      allStaffEventsForDay.forEach((event) => {
        const isStaff = ['Teacher', 'SchoolAdmin', 'DistrictAdmin'].includes(event.type);
        if (!isStaff) return;
        const isNewLogin = event.name === 'login';
        const isLegacy =
          new Date(event.time) <= lookupNewDBStartDate('peekapakLogin') &&
          (event.name === 'User Login' || event.name === 'login' || event.name === 'User Reactivated');
        if (isNewLogin || isLegacy) {
          educatorLoginEvents.push(event);
        }
      });
      const dayCounts = Array(7).fill(0);
      educatorLoginEvents.forEach((event) => {
        const day = new Date(event.time).toLocaleDateString('en-US', { weekday: 'long', timeZone: 'UTC' });
        const dayIndex = DAY_NAMES.indexOf(day);
        if (dayIndex >= 0) dayCounts[dayIndex]++;
      });
      const dayOfWeekRows = dayCounts
        .map((count, i) => [DAY_NAMES[i], count] as (string | number)[])
        .filter((row) => row[1] > 0)
        .sort((a, b) => (b[1] as number) - (a[1] as number));
      const dayOfWeekHeaders = ['Day', 'Logins'];
      if (dayOfWeekRows.length > 0) {
        const dayOfWeekData = [dayOfWeekHeaders, ...dayOfWeekRows];
        const dayOfWeekSheet = XLSX.utils.aoa_to_sheet(dayOfWeekData);
        XLSX.utils.book_append_sheet(workbook, dayOfWeekSheet, 'Day of Week Educators Login');
      }

      // Most Popular Games
      const gameAssociations = (associationData as { associations: Record<string, { displayName: string }> })
        .associations;
      const getDisplayName = (activityId: string): string => {
        const entry = gameAssociations[activityId];
        if (entry) return entry.displayName;
        const parts = activityId.match(/([A-Z]?[a-z]+)/g);
        if (parts) return parts.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        return activityId;
      };
      const gameCounts: Record<string, number> = {};
      Object.entries(state.gameProgressMap || {}).forEach(([, progress]) => {
        if (!Array.isArray(progress)) return;
        (progress as any[]).forEach((entry) => {
          if (!entry) return;
          const isCounted =
            (entry.type === 'quest' && entry.completionPercent > 0) ||
            (['forever', 'daily', 'finite'].includes(entry.type) && entry.timeSpent > 0);
          if (isCounted) {
            const name = getDisplayName(entry.displayName || entry.id || '');
            gameCounts[name] = (gameCounts[name] || 0) + 1;
          }
        });
      });
      const popularGames = Object.entries(gameCounts)
        .map(([name, count]) => [name, count] as (string | number)[])
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .slice(0, 5);
      const popularGamesHeaders = ['Game', 'Plays'];
      if (popularGames.length > 0) {
        const popularGamesData = [popularGamesHeaders, ...popularGames];
        const popularGamesSheet = XLSX.utils.aoa_to_sheet(popularGamesData);
        XLSX.utils.book_append_sheet(workbook, popularGamesSheet, 'Most Popular Games');
      }

      // Most Read Stories
      const bookNumberToDisplayName: Record<string, string> = {};
      Object.entries(gameAssociations).forEach(([key, entry]) => {
        if (key.startsWith('book_')) {
          bookNumberToDisplayName[key] = entry.displayName;
        }
      });
      const storyEvents = [...getLegacyStoryOpenEvents(allEvents), ...getNewStoryOpenEvents(allEvents)];
      const storyCounts: Record<string, number> = {};
      storyEvents.forEach((event) => {
        const evt = event as any;
        let name = event.name;
        const book = evt.book;
        if (book !== null && book !== undefined && bookNumberToDisplayName[`book_${book}`]) {
          name = bookNumberToDisplayName[`book_${book}`];
        } else if (evt.unit) {
          name = evt.unit;
        } else if (evt.story) {
          name = evt.story;
        } else if (evt.quest) {
          name = evt.quest;
        } else {
          const legacyMatch = event.name.match(/^story(\d+)pg1/);
          if (legacyMatch && bookNumberToDisplayName[`book_${legacyMatch[1]}`]) {
            name = bookNumberToDisplayName[`book_${legacyMatch[1]}`];
          } else {
            const featuredMatch = event.name.match(/^featuredstory[_ ](.+)/i);
            if (featuredMatch) {
              name = featuredMatch[1].replace(/[_-]/g, ' ');
            }
          }
        }
        storyCounts[name] = (storyCounts[name] || 0) + 1;
      });
      const mostReadStories = Object.entries(storyCounts)
        .map(([name, count]) => [name, count] as (string | number)[])
        .sort((a, b) => (b[1] as number) - (a[1] as number))
        .slice(0, 5);
      const mostReadStoriesHeaders = ['Story', 'Reads'];
      if (mostReadStories.length > 0) {
        const mostReadStoriesData = [mostReadStoriesHeaders, ...mostReadStories];
        const mostReadStoriesSheet = XLSX.utils.aoa_to_sheet(mostReadStoriesData);
        XLSX.utils.book_append_sheet(workbook, mostReadStoriesSheet, 'Most Read Stories');
      }

      const downMoodsHeaders = ['Teacher Name', 'Student Name', 'Down Mood Count'];
      const downMoodsRows: (string | number)[][] = [];

      Object.keys(studentsByTeacher).forEach((email) => {
        const students = studentsByTeacher[email] || [];
        const teacherObj = activeTeachers.find((t) => t.email === email);
        const teacherName = teacherObj ? `${teacherObj.firstName || ''} ${teacherObj.lastName || ''}`.trim() : email;
        students.forEach((student: any) => {
          const journals = state.journalsMap?.[student.userId] || [];
          const downMoodCount = journals.filter((j: any) => {
            const mood = j.properties?.mood || j.mood;
            return mood && ['Calm', 'Nervous', 'Sad', 'Scared', 'Angry', 'Surprised'].includes(mood);
          }).length;

          if (downMoodCount > 0) {
            downMoodsRows.push([teacherName, `${student.firstName} ${student.lastName || ''}`.trim(), downMoodCount]);
          }
        });
      });

      downMoodsRows.sort((a, b) => (b[1] as number) - (a[1] as number));

      if (downMoodsRows.length > 0) {
        const downMoodsData = [downMoodsHeaders, ...downMoodsRows];
        const downMoodsSheet = XLSX.utils.aoa_to_sheet(downMoodsData);
        XLSX.utils.book_append_sheet(workbook, downMoodsSheet, 'Students Down Moods');
      }

      const moodEvents = allEvents.filter((e) => e.name === 'journal-mood-log');

      const moodDataHeaders = ['Teacher Name', 'Student Name', 'Date', 'Mood', 'Notes'];
      const moodDataRows: (string | number)[][] = [];

      const studentIdSet = new Set<string>();
      const studentIdToTeacherMap: Record<string, string> = {};
      Object.keys(studentsByTeacher).forEach((email) => {
        const students = studentsByTeacher[email] || [];
        students.forEach((student: any) => {
          studentIdSet.add(student.userId);
          studentIdToTeacherMap[student.userId] = email;
        });
      });

      moodEvents.forEach((event: any) => {
        const eventUserId = event.userId || event.distinct_id;
        if (studentIdSet.has(eventUserId)) {
          let studentName = '';
          const teacherEmail = studentIdToTeacherMap[eventUserId] || '';
          let teacherName = '';
          Object.keys(studentsByTeacher).forEach((email) => {
            const students = studentsByTeacher[email] || [];
            students.forEach((student: any) => {
              if (student.userId === eventUserId) {
                studentName = `${student.firstName} ${student.lastName || ''}`.trim();
              }
            });
          });
          const teacherObj = activeTeachers.find((t) => t.email === teacherEmail);
          if (teacherObj) {
            teacherName = `${teacherObj.firstName || ''} ${teacherObj.lastName || ''}`.trim();
          }

          const mood = event.properties?.mood || event.mood;
          const timestamp = event.properties?.timestamp || event.timestamp || event.time;
          const dateStr = timestamp ? new Date(timestamp).toLocaleDateString() : '';
          const notes = event.properties?.journalEntry || event.properties?.note || event.note || '';

          moodDataRows.push([
            teacherName,
            studentName,
            dateStr,
            mood || '',
            typeof notes === 'string' ? notes : JSON.stringify(notes),
          ]);
        }
      });

      moodDataRows.sort((a, b) => {
        const dateA = a[1] as string;
        const dateB = b[1] as string;
        return dateB.localeCompare(dateA);
      });

      const moodData = [moodDataHeaders, ...moodDataRows];
      const moodDataSheet = XLSX.utils.aoa_to_sheet(moodData);
      XLSX.utils.book_append_sheet(workbook, moodDataSheet, 'Mood Data');

      const fileName = `${schoolName}-Report-${startDateStr}-to-${endDateStr}.xlsx`;
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Error generating Excel:', error);
    }
  }, [isReportLoading, state, dateRange]);

  if (!userProfile || !state.schoolsListReady) {
    return <SpinnerOverlay isShow />;
  }

  if (state.errorMessage) {
    return (
      <div className={styles.container}>
        <div className={styles.centeringContainer}>
          <div className={styles.titleAndDistrictSelector}>
            <div className={styles.titleAndReloadButton}>
              <h1>School Reporting</h1>
              <Icon className={styles.reloadIcon} icon='zondicons:reload' onClick={onReload} />
            </div>
            <DistrictSelector
              isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
              districtsList={state.districtsList}
              onSelectDistrict={onSelectDistrict}
              initialValue={state.selectedDistrict}
              currentlySelectedDistrict={state.selectedDistrict}
              disableChangeLocation={state.disableChangeLocation || false}
            />
          </div>
          <div className={styles.dateAndSubmit}>
            <DateRangePicker
              disabled={state.disableChangeLocation || false}
              dateRange={dateRange}
              setDateRange={setDateRange}
              startDate={startOfSchoolYear}
              excludeAfter={today}
              beforeFirstInputText='Start Date:'
              beforeSecondInputText='End Date:'
              isDisplayCalendar={isDisplayDayPicker}
              setIsDisplayCalendar={setIsDisplayDayPicker}
              handleBeforeStart={handleBeforeStart}
            />
            {/*<Icon
              className={styles.reloadIcon}
              icon='zondicons:reload'
              onClick={handleLoadData}
          />*/}
          </div>
        </div>
        <div className={styles.containerErrorMessage}>{state.errorMessage}</div>
      </div>
    );
  }

  logDebugLevel(`Render SchoolReport: ${state.selectedSchool} / ${state.selectedDistrict}`);
  //
  // SchoolReport main render function
  //
  return (
    <div className={styles.container}>
      <div className={styles.centeringContainer}>
        <div className={styles.districtAndSchoolSelectors}>
          <div className={styles.titleAndReloadButton}>
            <h1>School Reporting</h1>
            <Icon className={styles.reloadIcon} icon='zondicons:reload' onClick={onReload} />
            <Icon
              className={`${styles.reloadIcon} ${isReportLoading ? styles.disabled : ''}`}
              icon='zondicons:download'
              onClick={handleDownloadExcel}
            />
          </div>
          <DistrictSelector
            isPeekapakAdmin={!!isUserHasKey('KEY_PEEKAPAK_MANAGEMENT')}
            districtsList={state.districtsList}
            onSelectDistrict={onSelectDistrict}
            initialValue={state.selectedDistrict}
            currentlySelectedDistrict={state.selectedDistrict}
            disableChangeLocation={state.disableChangeLocation || false}
          />
          <SchoolSelector
            isDistrictAdmin={!!isUserHasKey('KEY_ADMINISTRATOR_REPORTS_SELECT_SCHOOL')}
            schoolsList={state.schoolsList}
            onSelectSchool={onSelectSchool}
            initialValue={state.selectedSchool}
            currentlySelectedSchool={state.selectedSchool}
            disableChangeLocation={state.disableChangeLocation || false}
          />
        </div>
        <div className={styles.dateAndSubmit}>
          <DateRangePicker
            dateRange={dateRange}
            setDateRange={setDateRange}
            startDate={dateRange.from || startOfSchoolYear}
            excludeAfter={today}
            beforeFirstInputText='Start Date:'
            beforeSecondInputText='End Date:'
            isDisplayCalendar={isDisplayDayPicker}
            setIsDisplayCalendar={setIsDisplayDayPicker}
            handleBeforeStart={handleBeforeStart}
          />
          {/*<Icon
              className={styles.reloadIcon}
              icon='zondicons:reload'
              onClick={handleLoadData}
          />*/}
          <div className={styles.eventType}>
            <CustomDropdown
              list={eventNames}
              onSelectSubject={setEventToChart}
              initialValue={eventToChart}
              currentlySelectedItem={eventToChart}
            />
          </div>
        </div>
        <SchoolChart
          legacyPeekavilleEvents={dateFilteredLegacyPeekavilleEvents}
          legacyPlatformEvents={dateFilteredLegacyPlatformEvents}
          newMixpanelEvents={dateFilteredNewMixpanelEvents}
          eventToChart={eventToChart}
          startDate={startDate}
          endDate={endDate}
          isLoading={
            !state.legacyPeekavilleEventsReady || !state.legacyPlatformEventsReady || !state.newMixpanelEventsReady
          }
          loadingStatus={state.loadingStatus}
        />
        <SchoolDashboard
          legacyPlatformEvents={dateFilteredLegacyPlatformEvents || []}
          legacyPlatformEventsReady={state.legacyPlatformEventsReady}
          errorMessage={state.errorMessage}
          journalsMap={dateFilteredJournals}
          journalsMapReady={state.journalsMapReady}
          newMixpanelEvents={dateFilteredNewMixpanelEvents}
          newMixpanelEventsReady={state.newMixpanelEventsReady}
          legacyPeekavilleEvents={dateFilteredLegacyPeekavilleEvents || []}
          legacyPeekavilleEventsReady={state.legacyPeekavilleEventsReady}
          selectedSchoolYear={state.selectedSchoolYear}
          teacherStudentMap={state.teacherStudentMap}
          teacherStudentMapReady={state.teacherStudentMapReady}
          teacherClassroomMaps={state.teacherClassroomMaps}
          teacherClassroomMapsReady={state.teacherClassroomMapsReady}
          userListReady={state.userListReady}
          userList={state.userList}
          gameProgressMap={state.gameProgressMap}
          gameProgressMapReady={state.gameProgressMapReady}
        />
        <div className={styles.tabSection}>
          <Tabs initialTab='Overview'>
            <Tab label='Overview'>
              <AnalyticsOverview
                legacyPlatformEvents={dateFilteredLegacyPlatformEvents}
                legacyPlatformEventsReady={state.legacyPlatformEventsReady}
                newMixpanelEvents={dateFilteredNewMixpanelEvents}
                newMixpanelEventsReady={state.newMixpanelEventsReady}
                errorMessage={state.errorMessage ? state.errorMessage : ''}
                legacyPeekavilleEvents={dateFilteredLegacyPeekavilleEvents}
                legacyPeekavilleEventsReady={state.legacyPeekavilleEventsReady}
                teacherStudentMap={state.teacherStudentMap}
                teacherStudentMapReady={state.teacherStudentMapReady}
              />
            </Tab>
            <Tab label='Staff'>
              <TeacherAnalytics
                legacyPlatformEvents={dateFilteredLegacyPlatformEvents}
                legacyPlatformEventsReady={state.legacyPlatformEventsReady}
                newMixpanelEvents={dateFilteredNewMixpanelEvents}
                newMixpanelEventsReady={state.newMixpanelEventsReady}
                errorMessage={state.errorMessage}
                gameProgressMap={state.gameProgressMap}
                gameProgressMapReady={state.gameProgressMapReady}
                legacyPeekavilleEvents={dateFilteredLegacyPeekavilleEvents}
                legacyPeekavilleEventsReady={state.legacyPeekavilleEventsReady}
                teacherStudentMap={state.teacherStudentMap}
                teacherStudentMapReady={state.teacherStudentMapReady}
                teacherClassroomMaps={state.teacherClassroomMaps}
                teacherClassroomMapsReady={state.teacherClassroomMapsReady}
                userList={state.userList.list}
                userListProgress={state.userListProgress}
                assignmentsMap={dateFilteredAssignments}
                assignmentsMapReady={state.assignmentsMapReady}
                assignmentsMapLoadProgress={state.assignmentsMapLoadProgress}
              />
            </Tab>
            <Tab label='Students'>
              <StudentAnalytics
                userList={state.userList.list}
                teacherStudentMap={state.teacherStudentMap}
                teacherStudentMapReady={state.teacherStudentMapReady}
                legacyPeekavilleEvents={dateFilteredLegacyPeekavilleEvents}
                legacyPeekavilleEventsReady={state.legacyPeekavilleEventsReady}
                newMixpanelEvents={dateFilteredNewMixpanelEvents}
                newMixpanelEventsReady={state.newMixpanelEventsReady}
                selectedSchool={state.selectedSchool}
                selectedSchoolYear={state.selectedSchoolYear}
                gameProgressMapReady={state.gameProgressMapReady}
                gameProgressMap={state.gameProgressMap}
                gameProgressMapLoadProgress={state.gameProgressMapLoadProgress}
                journalsMap={dateFilteredJournals}
                journalsMapReady={state.journalsMapReady}
                journalsMapLoadProgress={state.journalsMapLoadProgress}
                errorMessage={state.errorMessage}
                teacherClassroomMaps={state.teacherClassroomMaps}
                teacherClassroomMapsReady={state.teacherClassroomMapsReady}
                tableOverride={tableOverrideSuffix}
                legacyPlatformEvents={dateFilteredLegacyPlatformEvents}
                legacyPlatformEventsReady={state.legacyPlatformEventsReady}
                assignmentsMap={dateFilteredAssignments}
                assignmentsMapReady={state.assignmentsMapReady}
                assignmentsMapLoadProgress={state.assignmentsMapLoadProgress}
              />
            </Tab>
          </Tabs>
        </div>
      </div>
    </div>
  );

  async function onSelectDistrict(eventKey: string | null) {
    if (state.districtsListReady && state.districtsList.length > 0) {
      const selectedDistrict = eventKey || '';
      setDateRange(initialDateRange);
      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        ...getStudentAnalyticsInitialState(),
        selectedDistrict,
      }));
      const storageKey = 'selectedDistrict';
      await setLocalForage(storageKey, selectedDistrict);
    } else {
      console.warn(`%cDistrict List is Missing`, 'background: black; color:yellow');
    }
  }
  async function onSelectSchool(eventKey: string | null) {
    if (state.schoolsListReady && state.schoolsList.list.length > 0) {
      userManuallySelectedRef.current = true;
      const district = state.selectedDistrict || userProfile?.district;
      const selectedSchool = eventKey || '';
      setDateRange(initialDateRange);
      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getStudentAnalyticsInitialState(),
        selectedSchool,
      }));
      const storageKey = `${district}-selectedSchool`;
      await setLocalForage(storageKey, selectedSchool);
    } else {
      console.warn(`%cSchool List is Missing`, 'background: black; color:yellow');
    }
  }
  /*
  function onSelectSchoolYear(eventKey: string) {
    if (state.schoolsListReady && state.schoolsList.list.length > 0) {
      const selectedSchoolYear = eventKey;
      setState((prevState) => ({
        ...prevState,
        ...getCommonInitialState(),
        ...getSchoolInitialState(),
        ...getSchoolYearInitialState(),
        selectedSchoolYear,
      }));
    } else {
      console.warn(
        `%cSchool List is Missing`,
        'background: black; color:yellow'
      );
    }
  }
  */

  async function onReload() {
    await flushLocalForage();
    setState((prevState) => ({
      ...prevState,
      ...getCommonInitialState(),
      ...getStudentAnalyticsInitialState(),
      ...getSchoolYearInitialState(),
      ...getDistrictInitialState(),
    }));
  }
};

export default SchoolReport;
